<template>
    <LoaderComponent v-if="loading" />

    <div>
        <ShipmentHeader />

        <!-- Card Layout for Packages -->
        <div class="space-y-6 mt-4 ">
            <template v-if="packages.data && packages.data.length > 0">
                <div v-for="(pkg) in packages.data" :key="pkg.id"
                    class="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200">
                    <!-- Card Header -->
                    <div class="flex items-center justify-between bg-[#F8EACA] px-6 py-3 rounded-t-2xl">
                        <div>
                            <h2 class="text-lg font-semibold font-poppins text-gray-800">
                                Package ID: {{ pkg.id }}
                            </h2>
                            <p class="text-sm text-gray-600 mt-1">Date: {{ pkg.created_at || '2025-11-03' }}</p>
                        </div>

                        <div class="flex items-center gap-3">
                            <!-- Static Status Label -->
                            <span
                                class="text-sm font-semibold px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-300 shadow-sm">
                                Pending
                            </span>

                            <!-- Dropdown Button -->
                            <button :id="`dropdownMenuIconButton-${pkg.id}`" @click="toggleDropdown(pkg.id)"
                                class="p-2 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-gray-200 bg-white"
                                type="button">
                                <svg class="w-5 h-5 text-gray-700" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                                    fill="currentColor" viewBox="0 0 4 15">
                                    <path
                                        d="M3.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 6.041a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 5.959a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                                </svg>
                            </button>

                            <!-- Dropdown -->
                            <div v-if="openDropdownId === pkg.id"
                                class="absolute mt-40 right-8 z-20 bg-white divide-y divide-gray-100 rounded-lg shadow w-44">
                                <div class="py-2">
                                    <a href="#" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        @click="editPackage(pkg.id)">
                                        Edit
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Card Body -->
                    <div class="grid sm:grid-cols-3 gap-12 p-6">
                        <!-- Package Info -->
                        <div>
                            <h3
                                class="text-base font-semibold text-gray-800 mb-2 bg-[#F8EACA] px-3 py-1 rounded-md inline-block">
                                Package
                            </h3>
                            <template v-for="box in pkg.boxes" :key="box.id">
                                <div class="bg-gray-50 p-2 rounded-lg mb-2">
                                    <p class="text-xs text-gray-600 flex items-center gap-2">
                                        <span><b>Dimension:</b> {{ box.length }}×{{ box.width }}×{{ box.height }} {{
                                            box.dim_unit }}</span>
                                        <span class="text-gray-400">•</span>
                                        <span><b>Weight:</b> {{ box.weight }} {{ box.weight_unit }}</span>
                                    </p>
                                </div>
                            </template>

                            <template v-if="pkg.service_label">
                                <p class="text-sm text-gray-600 mt-2"><b>Label:</b> {{ pkg.service_label }}</p>
                                <p class="text-sm text-gray-600"><b>Code:</b> {{ pkg.service_code }}</p>
                                <p class="text-sm text-gray-600"><b>Type:</b> {{ pkg.package_type_code }}</p>
                            </template>
                        </div>

                        <!-- Sender Info -->
                        <div>
                            <h3
                                class="text-base font-semibold text-gray-800 mb-2 bg-[#F8EACA] px-3 py-1 rounded-md inline-block">
                                Sender
                            </h3>
                            <template v-if="pkg?.ship_from">
                                <p class="text-sm text-gray-700"><b>Name:</b> {{ pkg.ship_from.fullname }}</p>
                                <p class="text-sm text-gray-700"><b>Address:</b> {{ pkg.ship_from.address }}</p>
                                <p v-if="pkg.ship_from.address_2" class="text-sm text-gray-700"><b>Address 2:</b> {{
                                    pkg.ship_from.address_2 }}</p>
                                <p v-if="pkg.ship_from.address_3" class="text-sm text-gray-700"><b>Address 3:</b> {{
                                    pkg.ship_from.address_3 }}</p>
                                <p class="text-sm text-gray-700"><b>Zip code:</b> {{ pkg.ship_from.zip_code }}</p>
                                <p class="text-sm text-gray-700"><b>City:</b> {{ pkg.ship_from.city }}</p>
                                <p class="text-sm text-gray-700"><b>Country:</b> {{ pkg.ship_from.country?.name }}</p>
                            </template>
                        </div>

                        <!-- Receiver Info -->
                        <div>
                            <h3
                                class="text-base font-semibold text-gray-800 mb-2 bg-[#F8EACA] px-3 py-1 rounded-md inline-block">
                                Receiver
                            </h3>
                            <template v-if="pkg?.ship_to">
                                <p class="text-sm text-gray-700"><b>Name:</b> {{ pkg.ship_to.fullname }}</p>
                                <p class="text-sm text-gray-700"><b>Address:</b> {{ pkg.ship_to.address }}</p>
                                <p v-if="pkg.ship_to.address_2" class="text-sm text-gray-700"><b>Address 2:</b> {{
                                    pkg.ship_to.address_2 }}</p>
                                <p v-if="pkg.ship_to.address_3" class="text-sm text-gray-700"><b>Address 3:</b> {{
                                    pkg.ship_to.address_3 }}</p>
                                <p class="text-sm text-gray-700"><b>Zip code:</b> {{ pkg.ship_to.zip_code }}</p>
                                <p class="text-sm text-gray-700"><b>City:</b> {{ pkg.ship_to.city }}</p>
                                <p class="text-sm text-gray-700"><b>Country:</b> {{ pkg.ship_to.country?.name }}</p>
                            </template>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Empty State -->
            <div v-else class="flex flex-col items-center justify-center text-center p-6 mt-10"
                style="margin-bottom: 150px;">
                <h1 class="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                    There are no packages in draft.
                </h1>
                <img class="h-[65px] sm:h-[72px] w-[100px] sm:w-[120px] sm:object-contain mt-2" src="@/assets/logo.svg"
                    alt="No Orders" />
            </div>
        </div>

        <!-- Pagination -->
        <Pagination v-if="packages.data && packages.data.length > 0" @fetchRecords="fetchRecords"
            :pagination="packages" />
    </div>
</template>


<script>
import Pagination from "@/components/Global/Pagination.vue";
import LoaderComponent from "@/components/Partials/LoaderComponent.vue";
import ShipmentHeader from '@/components/Account/Shipment/ShipmentHeader.vue';

export default {
    name: "OrderDraft",
    data() {
        return {
            packages: [],
            loading: false,
            project_url: process.env.VUE_APP_PROJECT_URL,
            form: {
                package_id: "",
            },
            openDropdownId: null,
        };
    },
    components: { Pagination, LoaderComponent, ShipmentHeader },

    methods: {
        fetchRecords() {
            this.loading = true;
            const url = "package/index";

            var filters = {
                package_status_id: [2]
            }

            this.$axios
                .post(url, filters, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.packages = response.data.data.packages;
                    console.log(response);
                    this.loading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.loading = false;
                });
        },
        editPackage(package_id) {
            this.errors = {};
            this.loading = true;
            this.form.package_id = package_id;
            this.$axios
                .post("package/edit-package", this.form, {
                    headers: this.$headers
                })
                .then((response) => {
                    if (response.data.data.package_id) {
                        this.$router.push({ name: "account.shipments.edit", params: { id: response.data.data.package_id } })
                    } else {
                        this.$router.push({ name: "account.shipments" })
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleDropdown(id) {
            this.openDropdownId = this.openDropdownId === id ? null : id;
        },
    },
    mounted() {
        this.fetchRecords();
    }
};
</script>